import React, { useEffect, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import Navbar from '../setup/Navbar';
import HeadingText from '../../BuiltInComponent/HeadingTest';
import '../../services/GlobalVariables';

const displayTime = (value) => value ? dayjs(value).format('DD-MMM-YYYY hh:mm A') : '-';
const displayStatus = (value) => String(value || 'not set').replaceAll('_', ' ').toUpperCase();
const isLateDelivered = (row) => (
    Boolean(row.entryAt && row.deliveredAt)
    && dayjs(row.deliveredAt).diff(dayjs(row.entryAt), 'minute', true) > 60
);
const durationText = (start, end) => {
    if (!start || !end) return 'Pending / Not reached';
    const minutes = Math.max(0, Math.round(dayjs(end).diff(dayjs(start), 'minute', true)));
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`;
};

const timelineStages = [
    { label: 'Entry to Preparing', start: 'entryAt', end: 'preparingAt' },
    { label: 'Preparing to Ready', start: 'preparingAt', end: 'readyAt' },
    { label: 'Ready to Out For Delivery', start: 'readyAt', end: 'outForDeliveryAt' },
    { label: 'Out For Delivery to Delivered', start: 'outForDeliveryAt', end: 'deliveredAt' }
];

const DeliveryTimelineReport = () => {
    const [filters, setFilters] = useState({
        fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        toDate: dayjs().format('YYYY-MM-DD'),
        orderID: ''
    });
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);

    const loadReport = async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                fromDate: filters.fromDate,
                toDate: filters.toDate
            };
            if (String(filters.orderID).trim()) {
                params.orderID = String(filters.orderID).trim();
            }
            const response = await axios.get(`${global.SET_API_URL}delivery/timelineReport`, { params });
            setRows(response.data?.rows || []);
        } catch (requestError) {
            setRows([]);
            setError(requestError.response?.data?.message || 'Delivery timeline report load nahi ho saki.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadReport();
        // Initial report should load once; later requests use the View Report button.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateFilter = (field) => (event) => {
        setFilters((current) => ({ ...current, [field]: event.target.value }));
    };

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                <HeadingText text="Delivery Timeline Report" />

                <Paper sx={{ p: 2, mb: 2 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ md: 'center' }}
                    >
                        <TextField
                            type="date"
                            label="From Date"
                            value={filters.fromDate}
                            onChange={updateFilter('fromDate')}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            type="date"
                            label="To Date"
                            value={filters.toDate}
                            onChange={updateFilter('toDate')}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Order Number"
                            value={filters.orderID}
                            onChange={updateFilter('orderID')}
                            inputProps={{ inputMode: 'numeric' }}
                        />
                        <Button variant="contained" onClick={loadReport} disabled={loading}>
                            View Report
                        </Button>
                    </Stack>
                </Paper>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Paper>
                    <TableContainer sx={{ maxHeight: '72vh' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Order #</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Entry</TableCell>
                                    <TableCell>Preparing</TableCell>
                                    <TableCell>Ready</TableCell>
                                    <TableCell>Out For Delivery</TableCell>
                                    <TableCell>Delivered</TableCell>
                                    <TableCell>Current Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => {
                                    const lateDelivered = isLateDelivered(row);
                                    return (
                                    <TableRow
                                        hover
                                        key={row.orderID}
                                        onClick={() => setSelectedOrder(row)}
                                        sx={lateDelivered ? {
                                            backgroundColor: '#ffebee',
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: '#ffcdd2' }
                                        } : { cursor: 'pointer' }}
                                    >
                                        <TableCell sx={{ fontWeight: 700 }}>#{row.orderID}</TableCell>
                                        <TableCell>
                                            {row.customerName || '-'}
                                            {row.customerPhone ? ` (${row.customerPhone})` : ''}
                                        </TableCell>
                                        <TableCell>{displayTime(row.entryAt)}</TableCell>
                                        <TableCell>{displayTime(row.preparingAt)}</TableCell>
                                        <TableCell>{displayTime(row.readyAt)}</TableCell>
                                        <TableCell>{displayTime(row.outForDeliveryAt)}</TableCell>
                                        <TableCell sx={lateDelivered ? { color: 'error.main', fontWeight: 700 } : undefined}>
                                            {displayTime(row.deliveredAt)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={lateDelivered ? 'error' : 'default'}
                                                label={lateDelivered ? 'LATE DELIVERED' : displayStatus(row.currentStatus)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                    );
                                })}
                                {!loading && rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No delivery orders found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {loading && (
                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ p: 2 }}>
                            <CircularProgress size={20} />
                            <Typography>Loading...</Typography>
                        </Stack>
                    )}
                </Paper>

                <Dialog
                    open={Boolean(selectedOrder)}
                    onClose={() => setSelectedOrder(null)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        Order #{selectedOrder?.orderID} Status Duration
                    </DialogTitle>
                    <DialogContent dividers>
                        {selectedOrder && (
                            <Stack spacing={1.5}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography color="text.secondary">Customer</Typography>
                                    <Typography sx={{ fontWeight: 600 }}>
                                        {selectedOrder.customerName || '-'}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography color="text.secondary">Current Status</Typography>
                                    <Chip
                                        size="small"
                                        color={isLateDelivered(selectedOrder) ? 'error' : 'default'}
                                        label={isLateDelivered(selectedOrder)
                                            ? 'LATE DELIVERED'
                                            : displayStatus(selectedOrder.currentStatus)}
                                    />
                                </Stack>
                                <Divider />

                                {timelineStages.map((stage) => (
                                    <Paper key={stage.label} variant="outlined" sx={{ p: 1.5 }}>
                                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                                            <Box>
                                                <Typography sx={{ fontWeight: 600 }}>{stage.label}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {displayTime(selectedOrder[stage.start])}!� {displayTime(selectedOrder[stage.end])}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                {durationText(selectedOrder[stage.start], selectedOrder[stage.end])}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                ))}

                                <Paper
                                    sx={{
                                        p: 1.5,
                                        color: 'common.white',
                                        backgroundColor: isLateDelivered(selectedOrder) ? 'error.main' : 'primary.main'
                                    }}
                                >
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography sx={{ fontWeight: 700 }}>Total Delivery Time</Typography>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {durationText(selectedOrder.entryAt, selectedOrder.deliveredAt)}
                                        </Typography>
                                    </Stack>
                                </Paper>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSelectedOrder(null)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
};

export default DeliveryTimelineReport;
